import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Button, Typography, TextField, Chip, Paper, CircularProgress, Divider,
  Autocomplete, Fade, Tooltip, IconButton, useTheme, Tabs, Tab
} from "@mui/material";
import EmojiFoodBeverageIcon from '@mui/icons-material/EmojiFoodBeverage';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BoltIcon from "@mui/icons-material/Bolt";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { buildRecipePath } from "../utils/recipeUrl";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocationCtx } from "../context/LocationContext";
import LocationGate from "./LocationGate";
import FeatureGateDialog from "../components/modals/FeatureGateDialog";
import useFeatureGate from "../hooks/useFeatureGate";
import { premiumOptionSx } from "./common/autocompleteStyles";
import { userHasActiveEntitlement } from "../utils/entitlements";
import { trackEvent } from "../utils/analytics/gtag";

// Πιο ανθεκτικό check για αυγά
const isEgg = (name) => {
  if (typeof name !== "string") return false;
  return /egg|αυγό/i.test(name);
};

function getEggInfo(requiredGrams, userHasGrams) {
  const EGG_GRAMS = 68;
  const needed = Math.ceil((requiredGrams || 0) / EGG_GRAMS);
  const have = Math.floor((userHasGrams || 0) / EGG_GRAMS);
  const missing = Math.max(needed - have, 0);
  return { needed, have, missing };
}

// Μετατρέπει ένα i18n code σε backend suffix
function normalizeLangFrontend(code) {
  if (!code) return "en";
  const c = code.toLowerCase().replace("-", "_");
  const alias = {
    gr: "el", el: "el", el_gr: "el",
    en: "en", "en_gb": "en_gb", "en_us": "en_us",
    "en-uk": "en_gb", "en-gb": "en_gb", "en-us": "en_us",
    ar: "ar", bg: "bg", de: "de", es: "es", fr: "fr",
    hr: "hr", it: "it", ro: "ro", ru: "ru", sq: "sq",
    sr: "sr", sv: "sv", ur: "ur",
    zh: "zh", "zh_cn": "zh", "zh_tw": "zh", "zh-hans": "zh", "zh-hant": "zh",
  };
  if (alias[c]) return alias[c];
  const primary = c.split("_")[0];
  return alias[primary] || primary || "en";
}

function gpName(gp, langCode) {
  const suf = normalizeLangFrontend(langCode);
  return gp?.[`name_${suf}`] || gp?.name_el || gp?.name_en || gp?.name || "";
}


export default function QuickRecipePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { country } = useLocationCtx();

  const [openGate, setOpenGate] = useState(false);


  // Mode: 0 = νέο υλικό, 1 = από αποθήκη
  const [mode, setMode] = useState(0);

  const [input, setInput] = useState("");
  const [inputQty, setInputQty] = useState("");
  const [householdMembers, setHouseholdMembers] = useState("4");

  const [ingredients, setIngredients] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]);

  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState([]);

  const [results, setResults] = useState([]);
  const [matches, setMatches] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showMore, setShowMore] = useState(false);
  const [favLoadingId, setFavLoadingId] = useState(null);


  const [user, setUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(true);

  useEffect(() => {
    Promise.allSettled([api.users.getCurrentUser(), api.users.entitlements()])
      .then(([userResult, entitlementsResult]) => {
        if (userResult.status !== "fulfilled") {
          setUser(null);
          return;
        }
        const res = userResult.value;
        const u = res.data?.data || res.data?.user || res.data;
        const entitlements = entitlementsResult.status === "fulfilled"
          ? entitlementsResult.value?.data?.entitlements || null
          : null;
        setUser(u ? { ...u, entitlements } : null);
      })
      .catch(() => setUser(null))
      .finally(() => setCheckingUser(false));
  }, []);

  const isLoggedIn = !!user?.id && !user?.is_guest;
  const isPremium = userHasActiveEntitlement(user);

  // Get lang for links
  const lang = params.lang || i18n.language || "el";

  const { gateOpen, gateConfig, gateActions } = useFeatureGate({
    isLoggedIn,
    isPremium,
    lang,
    navigate,
  });

  const membersNum = useMemo(() => {
    const n = parseInt(householdMembers, 10);
    return Number.isFinite(n) && n > 0 ? n : 4;
  }, [householdMembers]);

  // Load all generic products (for autocomplete)
  useEffect(() => {
    api.genericProducts.list({ params: { lang: i18n.language } })
      .then(res => {
        const payload = res?.data;
        const arr = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);
        setAllIngredients(arr);
      })
      .catch(() => setAllIngredients([]));
  }, [i18n.language]);

  // Load inventory for inventory mode
  useEffect(() => {
    if (mode === 1) {
      api.inventory.list()
        .then(res => setInventoryProducts(res.data?.data || []))
        .catch(() => setInventoryProducts([]));
    }
  }, [mode]);

  // Update ingredients according to mode and selections
  useEffect(() => {
    if (mode === 1) {
      const selected = (inventoryProducts || [])
        .filter(prod => selectedInventoryIds.includes(prod.id))
        .map(prod => ({
          name: prod.product_name,
          qty: Math.round(Number(prod.purchased_quantity || 1)) || 1,
          // πολύ σημαντικό: κρατάμε generic_product_id αν υπάρχει
          generic_product_id: prod.generic_product_id || null
        }));
      setIngredients(selected);
    }
  }, [selectedInventoryIds, inventoryProducts, mode]);

  useEffect(() => {
    localStorage.setItem("user_ingredients", JSON.stringify(ingredients));
  }, [ingredients]);

  const handleRemoveIngredient = (ingredient) => {
    setIngredients(prev => prev.filter((ing) => ing.name !== ingredient.name));
    if (mode === 1) {
      const inv = (inventoryProducts || []).find(prod => prod.product_name === ingredient.name);
      if (inv) setSelectedInventoryIds(ids => ids.filter(id => id !== inv.id));
    }
  };

  // Options για autocomplete
  const ingredientOptions = (Array.isArray(allIngredients) ? allIngredients : [])
    .map(gp => ({ id: gp.id, label: gpName(gp, i18n.language) || "" }))
    .filter(o => o.label);

  // Κρατάμε και το id που διάλεξε ο χρήστης από Autocomplete
  const [selectedGenericId, setSelectedGenericId] = useState(null);

  const handleAddIngredient = () => {
    const name = String(input || "").trim();
    let qty = parseInt(inputQty, 10) || 1;
    if (!name) return;

    if (isEgg(name)) qty = qty * 68;

    const fallback = (Array.isArray(allIngredients) ? allIngredients : [])
      .find(gp => gpName(gp, i18n.language) === name)?.id || null;

    setIngredients(prev => [
      ...prev,
      { name, qty, generic_product_id: selectedGenericId ?? fallback }
    ]);

    setInput("");
    setInputQty("");
    setSelectedGenericId(null);
    setError("");
  };

  const handleToggleInventory = (prodId) => {
    setSelectedInventoryIds(ids =>
      ids.includes(prodId) ? ids.filter(id => id !== prodId) : [...ids, prodId]
    );
    setError("");
  };

  const handleSearch = async () => {
    if (!country) {
      setOpenGate(true);
      return;
    }
    if (mode === 1) {
      trackEvent("inventory_use", {
        items_count: selectedInventoryIds.length,
        is_premium: isPremium,
      });
    }
    trackEvent("quick_recipe_search", {
      mode: mode === 0 ? "new" : "inventory",
      ingredients_count: ingredients?.length || 0,
      members: membersNum,
      country: country || "",
      lang: i18n.language || "en",
    });
    setLoading(true);
    setError("");
    setResults([]);
    setShowMore(false);
    setMatches({});

    try {
      const safeIngredients = (ingredients || []).map(ing => ({
        name: ing.name,
        qty: parseInt(ing.qty, 10) || 1,
        generic_product_id: ing.generic_product_id || null,
      }));

      const res = await api.recipes.findByIngredients(
        safeIngredients, i18n.language, null, membersNum
      );

      setMatches(res.data?.matches || {});
      const allNull = Object.values(res.data?.matches || {}).every(v => v === null);

      if (allNull) {
        setError(res.data?.message || t("try_select_from_list"));
        setResults([]);
      } else {
        setResults(res.data?.data || []);
      }
    } catch (e) {
      setError(t("search_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async (recipeId, isFav) => {
    if (
      !gateActions.guardAuth({
        title: t("favorites.require_account.title", "Save to Favorites"),
        message: t(
          "favorites.require_account.body",
          "To save favorites, please log in or create an account."
        ),
      })
    )
      return;

    setFavLoadingId(recipeId);
    try {
      if (isFav) await api.favorites.remove(recipeId);
      else await api.favorites.add({ recipe_id: recipeId });

      setResults((prev) =>
        prev.map((r) => (r.id === recipeId ? { ...r, is_favorite: !isFav } : r))
      );
    } catch (e) { }
    setFavLoadingId(null);
  };


  const fuzzySuggestions = Object.entries(matches)
    .filter(([inp, match]) => match && match.score < 100)
    .map(([inp, match]) => (
      <Typography key={inp} sx={{ mt: 1, fontSize: "0.97rem", textAlign: "left" }}>
        {t("for_ingredient")} <b>{inp}</b> {t("found_similar")}:
        <b style={{ color: theme.palette.success.main, marginLeft: 6 }}>{match.name}</b>{" "}
        {`(${match.score.toFixed(0)}%)`}
      </Typography>
    ));

  // ✅ ΝΕΟ: σύνδεση με RecipeDetails page (premium/ομοιόμορφο)
  const handleOpenRecipePage = (recipe) => {
    if (!recipe?.id) return;
    trackEvent("open_recipe_from_quick", {
      recipe_id: recipe?.id,
      recipe_slug: recipe?.slug || undefined,
      lang,
    });

    navigate(buildRecipePath(recipe, lang), {
      state: {
        from: "quick_recipe",
        quickIngredients: ingredients,
        members: membersNum,
      },
    });
  };

  // ✅ FIX: render ads correctly
  // const renderInFeedAd = (index) => {
  //   if (!showAds) return null;
  //   if (index === 2 || index === 6) {
  //     return (
  //       <Box sx={{ my: 2 }}>
  //         <AdSenseAd slot="2080997069" isPremium={isPremium} />
  //       </Box>
  //     );
  //   }
  //   return null;
  // };

  const maxToShow = showMore ? results.length : (isMobile ? 4 : 6);

  return (
    <Box sx={{
      maxWidth: { xs: 640, md: 850 },
      mx: "auto",
      width: "100%",
      mt: 5,
      p: { xs: 1.5, sm: 3 },
      textAlign: "center",
      bgcolor: "background.paper",
      borderRadius: 4,
      boxShadow: 4,
      position: "relative",
      overflow: "visible"
    }}>
      {/* Hero */}
      <Box sx={{ mb: 3 }}>
        <BoltIcon sx={{ fontSize: 44, color: "secondary.main", mb: 1 }} />
        <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: 0.5, mb: 1 }}>
          {t("quick_recipe_page_title", "Βρες συνταγή με ένα υλικό")}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "text.secondary", mb: 2 }}>
          {t("quick_recipe_page_desc", "Γράψε ένα υλικό, βρες τι μπορείς να μαγειρέψεις αμέσως!")}
        </Typography>

        {/* {showAds && (
          <Box sx={{ my: 2 }}>
            <AdSenseAd slot="2080997069" isPremium={isPremium} />
          </Box>
        )} */}
      </Box>

      {openGate && (
        <LocationGate forceOpen onClose={() => setOpenGate(false)} />
      )}

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={mode}
          onChange={(_, v) => {
            setMode(v);
            setIngredients([]);
            setError("");
            setInput("");
            setInputQty("");
            setSelectedInventoryIds([]);
            setResults([]);
            setMatches({});
          }}
          centered
          sx={{
            mb: 2,
            borderRadius: 3,
            boxShadow: 2,
            maxWidth: 520,
            mx: "auto",
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"
          }}
        >
          <Tab icon={<EmojiFoodBeverageIcon />} iconPosition="start" label={t("add_new_ingredient", "Νέο υλικό")} />
          <Tab icon={<WarehouseIcon />} iconPosition="start" label={t("choose_from_inventory", "Αποθήκη")} />
        </Tabs>

        {/* Mode 0 */}
        {mode === 0 && (
          <Paper
            elevation={isMobile ? 0 : 3}
            sx={{
              borderRadius: 4,
              p: { xs: 1.5, sm: 3 },
              mb: 1.5,
              background: theme.palette.mode === "dark"
                ? "linear-gradient(120deg, rgba(37,44,56,0.95) 60%, rgba(54,76,94,0.95) 100%)"
                : "linear-gradient(120deg, #f6fafb 60%, #ecf8f3 100%)",
              boxShadow: isMobile ? "none" : "0 8px 24px rgba(73,188,144,0.14)"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: { xs: "stretch", sm: "flex-end" },
                justifyContent: "center",
                width: "100%",
                maxWidth: 650,
                mx: "auto"
              }}
            >
              <Autocomplete
                freeSolo
                options={ingredientOptions}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : (opt?.label || ""))}
                inputValue={input}
                value={null}
                onInputChange={(_, val) => {
                  setInput(val || "");
                  setSelectedGenericId(null);
                }}
                onChange={(_, val) => {
                  if (val && typeof val !== "string") {
                    setInput(val.label || "");
                    setSelectedGenericId(val.id ?? null);
                  }
                }}
                sx={{ flex: 2, minWidth: 0 }}
                renderOption={(props, option) => {
                  const label = typeof option === "string" ? option : option?.label;
                  return (
                    <Box component="li" {...props} sx={premiumOptionSx(theme)}>
                      {label}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("ingredient") || "Υλικό"}
                    placeholder={t("try_select_from_list") || "Ξεκίνα να γράφεις…"}
                    fullWidth
                  />
                )}
              />

              <TextField
                label={isEgg(String(input || "")) ? t("egg_count", "Αριθμός αυγών") : t("quantity")}
                type="number"
                value={inputQty}
                onChange={e => {
                  const val = e.target.value;
                  if (/^\d{0,4}$/.test(val)) setInputQty(val);
                }}
                inputProps={{ min: 1, max: 9999, style: { fontSize: 18 } }}
                sx={{ flex: 1, minWidth: 0 }}
                fullWidth
              />

              <Button
                variant="contained"
                onClick={handleAddIngredient}
                disabled={!String(input || "").trim()}
                sx={{
                  px: 3,
                  height: { xs: 48, sm: "100%" },
                  fontWeight: 900,
                  borderRadius: 2,
                  mt: { xs: 1, sm: 0 }
                }}
                startIcon={<EmojiFoodBeverageIcon />}
                fullWidth={isMobile}
              >
                {t("add")}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Mode 1 */}
        {mode === 1 && (
          <Paper sx={{
            p: 2, mb: 1.5, borderRadius: 4, boxShadow: 3,
            background: theme.palette.mode === "dark"
              ? "linear-gradient(120deg, rgba(35,42,55,0.95) 60%, rgba(54,76,94,0.95) 100%)"
              : "linear-gradient(120deg, #f6fafb 60%, #e2fff3 100%)",
            minHeight: 110
          }}>
            {inventoryProducts.length === 0 ? (
              <Typography color="text.secondary">
                {t("no_inventory_products", "Δεν έχεις προϊόντα στην αποθήκη σου.")}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                {inventoryProducts.map(prod => (
                  <Chip
                    key={prod.id}
                    label={`${prod.product_name} (${Math.round(Number(prod.purchased_quantity || 0)) || 1})`}
                    color={selectedInventoryIds.includes(prod.id) ? "primary" : "default"}
                    onClick={() => handleToggleInventory(prod.id)}
                    sx={{ fontWeight: 700 }}
                    variant={selectedInventoryIds.includes(prod.id) ? "filled" : "outlined"}
                    clickable
                  />
                ))}
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Selected ingredients */}
      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
        {ingredients.map((ing) => (
          <Chip
            key={`${ing.name}-${ing.qty}-${ing.generic_product_id || "x"}`}
            label={
              isEgg(ing.name)
                ? `${ing.name} (${Math.round(Number(ing.qty || 0) / 68) || 1})`
                : `${ing.name} (${ing.qty})`
            }
            onDelete={() => handleRemoveIngredient(ing)}
            color="primary"
            sx={{ mx: 0.5, my: 0.5, fontWeight: 900 }}
          />
        ))}
      </Box>

      {fuzzySuggestions.length > 0 && (
        <Box sx={{ mb: 2 }}>{fuzzySuggestions}</Box>
      )}

      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}

      {/* Members + Search */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 3, flexWrap: "wrap" }}>
        <TextField
          label={t("people")}
          type="number"
          value={householdMembers}
          onChange={e => {
            const val = e.target.value;
            if (/^\d{0,2}$/.test(val)) setHouseholdMembers(val);
          }}
          onBlur={() => {
            if (!householdMembers) setHouseholdMembers("4");
          }}
          inputProps={{ min: 1, max: 20, style: { fontSize: 18, textAlign: "center" } }}
          sx={{ width: 120 }}
          placeholder="4"
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          sx={{ px: 4, py: 1.1, fontWeight: 900, fontSize: "1.06rem", borderRadius: 2.5 }}
          disabled={ingredients.length === 0 || loading}
          startIcon={<EmojiFoodBeverageIcon />}
        >
          {t("find_recipes")}
        </Button>
      </Box>

      {loading && <CircularProgress sx={{ mt: 2 }} />}

      {/* Results */}
      {!loading && results.length > 0 && (
        <Fade in>
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={900}>
              {t("recommended_recipes")}
            </Typography>

            {results.slice(0, maxToShow).map((rec, idx) => (
              <React.Fragment key={rec.id}>
                <Paper sx={{ my: 1.5, p: 2, textAlign: "left", position: "relative", borderRadius: 3, boxShadow: 2 }}>
                  <Tooltip title={isLoggedIn ? t("add_to_favorites") : t("login_to_save")}>
                    <span>
                      <IconButton
                        color="error"
                        disabled={checkingUser || favLoadingId === rec.id}
                        onClick={() => handleFavorite(rec.id, rec.is_favorite)}
                        sx={{ position: "absolute", top: 12, right: 12 }}
                      >
                        {rec.is_favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmojiFoodBeverageIcon color="secondary" />
                    <Typography variant="h6" fontWeight={900}>
                      {rec.name}
                    </Typography>
                  </Box>

                  {Array.isArray(rec.ingredients) && (
                    <Typography sx={{ mt: 0.6, opacity: 0.9 }}>
                      {t("ingredients_for_one_person")}:{" "}
                      {rec.ingredients.map(i => `${i.name} (${i.quantity}γρ)`).join(", ")}
                    </Typography>
                  )}

                  {Array.isArray(rec.missing_ingredients) && rec.missing_ingredients.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography color="error" sx={{ fontWeight: 700 }}>
                        {t("missing")}:{" "}
                        {rec.missing_ingredients.map((item, k) => {
                          if (typeof item === "object" && isEgg(item.name)) {
                            const eggInfo = getEggInfo(item.required, item.user_has);
                            return (
                              <span key={k}>
                                {item.name}
                                {` (${t("required")}: ${eggInfo.needed}, ${t("have")}: ${eggInfo.have}, ${t("missing")}: ${eggInfo.missing})`}
                                {k < rec.missing_ingredients.length - 1 ? ", " : ""}
                              </span>
                            );
                          }
                          if (typeof item === "object") {
                            return (
                              <span key={k}>
                                {item.name}
                                {item.required ? ` (${t("required")}: ${item.required}γρ, ${t("have")}: ${item.user_has || 0}γρ)` : ""}
                                {k < rec.missing_ingredients.length - 1 ? ", " : ""}
                              </span>
                            );
                          }
                          return (
                            <span key={k}>
                              {String(item)}
                              {k < rec.missing_ingredients.length - 1 ? ", " : ""}
                            </span>
                          );
                        })}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {/* <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleShowDetails(rec)}
                      sx={{ fontWeight: 900, borderRadius: 2 }}
                    >
                      {t("details")}
                    </Button> */}

                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleOpenRecipePage(rec)}
                      endIcon={<OpenInNewIcon />}
                      sx={{ fontWeight: 900, borderRadius: 2 }}
                    >
                      {t("open_recipe", "Άνοιγμα")}
                    </Button>
                  </Box>
                </Paper>

                {/* {renderInFeedAd(idx)} */}
              </React.Fragment>
            ))}

            {results.length > maxToShow && !showMore && (
              <Button onClick={() => setShowMore(true)} variant="outlined" sx={{ mt: 2, fontWeight: 900, borderRadius: 2 }}>
                {t("see_more_results")}
              </Button>
            )}
          </Box>
        </Fade>
      )}

      {/* <RecipeDetailDialog
        open={detailOpen && !!selectedRecipe}
        onClose={() => setDetailOpen(false)}
        recipe={selectedRecipe}
        userIngredients={ingredients}
        householdMembers={membersNum}
        lang={i18n.language}
        onShowShoppingList={() => {
          if (selectedRecipe?.id) navigate(`/${lang}/shopping/list?preview=${selectedRecipe.id}&members=${membersNum}`);
        }}
        onRequireAuth={() => setShowAuthDialog(true)}
        handleFavorite={(recipeId, isFav) => {
          setResults(prev => prev.map(r =>
            r.id === recipeId ? { ...r, is_favorite: !isFav } : r
          ));
        }}
      /> */}
      <FeatureGateDialog
        open={gateOpen}
        onClose={gateActions.close}
        lang={lang}
        isLoggedIn={isLoggedIn}
        require={gateConfig.require}
        title={gateConfig.title}
        message={gateConfig.message}
        onLogin={gateActions.onLogin}
        onRegister={gateActions.onRegister}
        onUpgrade={gateActions.onUpgrade}
      />
    </Box>
  );
}
